import { faSync } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useConfig } from '@nexelem/config';
import { Badge, Button } from '@nexelem/components';
import { format } from 'date-fns';
import { useCustomTranslation } from '../../../../hooks';
import { useAPI } from '@nexelem/api';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCustomToast } from '@nexelem/snackbar';
import { useAuthContext } from '../../../../providers/AuthProvider';
import { PERMS } from '../../../../routes/permissions';

const LAST_SYNC_UPDATE_QUERY_KEY = 'last-sync-update';

const useLastSyncUpdate = () => {
  const api = useAPI();

  return useQuery({
    queryKey: [LAST_SYNC_UPDATE_QUERY_KEY],
    queryFn: () => api.getBackErpSynchronizationFetchLastExecutions(),
    select: (date) => {
      const timestamp = Date.parse(date);

      if (isNaN(timestamp) === false) {
        return format(new Date(date), 'yyyy-MM-dd HH:mm');
      }

      return;
    },
  });
};

const useExternalSyncMutation = () => {
  const api = useAPI();
  const queryClient = useQueryClient();
  const displayToast = useCustomToast();
  const { tWrapper } = useCustomTranslation('components.toasts.commonMessage');
  return useMutation({
    mutationFn: () => api.getExternalSynchronizationRunSaga(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [LAST_SYNC_UPDATE_QUERY_KEY] });
    },
    onError: () => {
      displayToast({
        toastType: 'error',
        message: tWrapper('error'),
      });
    },
  });
};

export const SyncButton = () => {
  const { tWrapper } = useCustomTranslation('products.productionPlan');
  const { modules } = useConfig();
  const isSyncButtonEnabled = modules.production.syncButton;
  const { data: lastSyncUpdate } = useLastSyncUpdate();
  const externalSync = useExternalSyncMutation();
  const { hasPermission } = useAuthContext();
  const canSynchronize = hasPermission(PERMS.PERM_77);

  if (!isSyncButtonEnabled || !canSynchronize) {
    return null;
  }

  return (
    <div className="flex items-center gap-4">
      {lastSyncUpdate && (
        <Badge StartIcon={<FontAwesomeIcon icon={faSync} />}>
          {tWrapper('lastSynchronization')} {lastSyncUpdate}
        </Badge>
      )}
      <Button
        isLoading={externalSync.isPending}
        StartIcon={<FontAwesomeIcon icon={faSync} />}
        variant="text"
        onClick={() => externalSync.mutate()}
      >
        {tWrapper('synchronize')}
      </Button>
    </div>
  );
};
